/**
 * MobileNews — Full mobile news hub
 * Tabs: News | Games | Trending | Injuries
 * News sub-tabs: My Team | Free Agents | All MLB
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme, dynastyTileStyle } from '../../../services/colorService';
import { mobileTileStyle } from '../../../services/mobileStyleService';
import { mlbAPI, leaguesAPI } from '../../../services/apiService';
import { fetchProbablePitchers } from '../../../services/probablePitchersApi';
import MobileLeagueNavigation from '../components/MobileLeagueNavigation';
import MobilePageHeader from '../components/MobilePageHeader';
import useDefaultLeague from '../../../hooks/useDefaultLeague';

const C = dynastyTheme.tokens.colors;

// ============================================================
// HELPERS
// ============================================================
const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ============================================================
// TAB BAR
// ============================================================
const TabBar = ({ tabs, active, onChange }) => (
  <div style={{
    display: 'flex',
    gap: '0',
    padding: '0 8px',
    marginBottom: '12px',
    borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
  }}>
    {tabs.map(t => (
      <button
        key={t.id}
        onClick={() => onChange(t.id)}
        style={{
          flex: 1,
          padding: '8px 0',
          fontSize: '13px',
          fontWeight: active === t.id ? '700' : '500',
          color: active === t.id
            ? dynastyTheme.tokens.colors.primary
            : dynastyTheme.tokens.colors.neutral[400],
          background: 'none',
          border: 'none',
          borderBottom: active === t.id
            ? `2px solid ${dynastyTheme.tokens.colors.primary}`
            : '2px solid transparent',
          cursor: 'pointer',
        }}
      >
        {t.label}
      </button>
    ))}
  </div>
);

// ============================================================
// SECTION WRAPPER
// ============================================================
const Section = ({ title, children, maxHeight }) => (
  <div style={{ padding: '0 8px', marginBottom: '14px' }}>
    {title && (
      <h3 style={{
        fontSize: '15px',
        fontWeight: '700',
        color: dynastyTheme.tokens.colors.neutral[50],
        marginBottom: '8px',
      }}>
        {title}
      </h3>
    )}
    <div
      className="home-scroll"
      style={{
        ...mobileTileStyle,
        ...(maxHeight ? {
          maxHeight,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        } : { overflow: 'hidden' }),
      }}
    >
      {children}
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <div style={{
    padding: '20px',
    textAlign: 'center',
    color: dynastyTheme.tokens.colors.neutral[500],
    fontSize: '12px',
  }}>
    {text}
  </div>
);

const Spinner = ({ text }) => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <div style={{
      width: '24px', height: '24px',
      border: `2px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
      borderTop: `2px solid ${dynastyTheme.tokens.colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 8px',
    }} />
    <p style={{ color: dynastyTheme.tokens.colors.neutral[500], fontSize: '12px' }}>{text}</p>
  </div>
);

// ============================================================
// NEWS TAB CONTENT
// ============================================================
const NewsNoteRow = ({ note, showBadge }) => {
  const [expanded, setExpanded] = useState(false);
  const content = note.content || note.body || '';

  return (
    <div
      onClick={() => setExpanded(prev => !prev)}
      style={{
        padding: '8px 12px',
        borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}30`,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        {/* Color bar */}
        <div style={{
          width: '3px', height: '32px', borderRadius: '2px', flexShrink: 0,
          backgroundColor: note.note_type === 'injury'
            ? dynastyTheme.tokens.colors.error
            : dynastyTheme.tokens.colors.primary,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              fontSize: '13px', fontWeight: '700',
              color: dynastyTheme.tokens.colors.neutral[50],
            }}>
              {note.player_name || note.player || 'Unknown'}
            </span>
            {showBadge && (
              <span style={{
                fontSize: '8px', fontWeight: '700',
                color: C.white,
                backgroundColor: dynastyTheme.tokens.colors.success,
                padding: '1px 4px', borderRadius: '2px',
              }}>FA</span>
            )}
            {note.note_type === 'injury' && (
              <span style={{
                fontSize: '8px', fontWeight: '700',
                color: C.white,
                backgroundColor: dynastyTheme.tokens.colors.error,
                padding: '1px 4px', borderRadius: '2px',
              }}>INJ</span>
            )}
          </div>
        </div>
        <span style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[500], flexShrink: 0 }}>
          {formatDate(note.date || note.published_date)}
        </span>
      </div>
      <p style={{
        fontSize: '11px', color: dynastyTheme.tokens.colors.neutral[300],
        lineHeight: '1.4', marginLeft: '9px',
        ...(expanded ? {} : {
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }),
      }}>
        {content}
      </p>
      {!expanded && content.length > 150 && (
        <span style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.primary, marginLeft: '9px' }}>
          Tap to read more
        </span>
      )}
    </div>
  );
};

const NewsTabContent = ({ leagues, defaultLeagueId }) => {
  const [subTab, setSubTab] = useState('team');
  const [allNotes, setAllNotes] = useState([]);
  const [faNews, setFaNews] = useState([]);
  const [rosterPlayerIds, setRosterPlayerIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const leagueId = defaultLeagueId || (leagues?.length > 0 ? leagues[0].league_id : null);

  useEffect(() => {
    loadNewsData();
  }, [leagueId]);

  const loadNewsData = async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        mlbAPI.getPlayerNotes().catch(() => []),
        leagueId ? leaguesAPI.getFreeAgentNews(leagueId).catch(() => []) : [],
      ]);

      // All player notes — backend returns array directly
      const notesResult = results[0];
      setAllNotes(Array.isArray(notesResult) ? notesResult : []);

      // Free agent news — may be array or { news: [] }
      const faResult = results[1];
      const fa = Array.isArray(faResult) ? faResult : (faResult?.news || faResult?.data || []);
      setFaNews(Array.isArray(fa) ? fa : []);

      // Get roster player IDs for "My Team" filtering
      if (leagues?.length > 0) {
        const league = leagues.find(l => l.league_id === leagueId) || leagues[0];
        if (league?.team_id && leagueId) {
          try {
            const rosterRes = await leaguesAPI.getTeamRosterCanonical(leagueId, league.team_id);
            const roster = rosterRes?.roster || rosterRes?.players || [];
            const ids = new Set(roster.map(p => {
              const mlbId = p.ids?.mlb || p.player_id || p.mlb_id;
              return mlbId ? Number(mlbId) : null;
            }).filter(Boolean));
            setRosterPlayerIds(ids);
          } catch {
            setRosterPlayerIds(new Set());
          }
        }
      }
    } catch (err) {
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotes = useMemo(() => {
    if (subTab === 'team') {
      return allNotes.filter(n => rosterPlayerIds.has(Number(n.player_id)));
    }
    return allNotes;
  }, [subTab, allNotes, rosterPlayerIds]);

  if (loading) return <Spinner text="Loading news..." />;

  return (
    <>
      {/* Sub-tabs */}
      <TabBar
        tabs={[
          { id: 'team', label: 'My Team' },
          { id: 'fa', label: 'Free Agents' },
          { id: 'all', label: 'All MLB' },
        ]}
        active={subTab}
        onChange={setSubTab}
      />
      <Section>
        {subTab === 'fa' ? (
          faNews.length > 0 ? faNews.map((n, i) => (
            <NewsNoteRow key={i} note={n} showBadge={true} />
          )) : <EmptyState text="No free agent news" />
        ) : (
          filteredNotes.length > 0 ? filteredNotes.slice(0, 30).map((n, i) => (
            <NewsNoteRow key={i} note={n} showBadge={false} />
          )) : <EmptyState text={subTab === 'team' ? 'No news for your roster' : 'No player news'} />
        )}
      </Section>
    </>
  );
};

// ============================================================
// GAMES TAB CONTENT
// ============================================================
const PitcherBadge = ({ pitcher }) => {
  if (!pitcher) return null;
  const name = pitcher.name || 'TBD';
  const owned = pitcher.team_name;
  const isFa = pitcher.is_free_agent !== false && !owned;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
      <span style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200] }}>{name}</span>
      {owned ? (
        <span style={{ fontSize: '7px', fontWeight: 700, padding: '1px 3px', borderRadius: 2, color: C.white, backgroundColor: dynastyTheme.tokens.colors.info }}>{owned.substring(0, 10)}</span>
      ) : isFa ? (
        <span style={{ fontSize: '7px', fontWeight: 700, padding: '1px 3px', borderRadius: 2, color: C.white, backgroundColor: dynastyTheme.tokens.colors.success }}>FA</span>
      ) : null}
    </span>
  );
};

const formatGameDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return dateStr;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const prefix = isTomorrow ? 'Tomorrow — ' : '';
  return `${prefix}${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
};

// 9n: mobile-internal helper. Mirrors the desktop buildStatLine semantics so the
// embedded preview shows stat lines too (per Cluster C adjacent fold-in). NO
// FALLBACKS — null fields are omitted, never replaced with sentinels.
const buildProbableStatLine = (pitcher) => {
  if (!pitcher || pitcher.era == null) return null;
  const parts = [];
  if (pitcher.wins != null && pitcher.losses != null) parts.push(`${pitcher.wins}-${pitcher.losses}`);
  if (pitcher.era != null) parts.push(`${Number(pitcher.era).toFixed(2)} ERA`);
  if (pitcher.whip != null) parts.push(`${Number(pitcher.whip).toFixed(2)} WHIP`);
  if (pitcher.strikeouts_pitched != null) parts.push(`${pitcher.strikeouts_pitched} K`);
  return parts.length > 0 ? parts.join(' · ') : null;
};

// 9n: vertical pitcher row with name + ownership pill + optional stat line.
// Used inside the embedded "Upcoming Probables" preview. TBD pitchers render
// with no stat line (NO FALLBACKS — no fake stats).
const ProbablePitcherDisplay = ({ pitcher }) => {
  if (!pitcher) return null;
  const name = pitcher.name || 'TBD';
  const isTbd = !pitcher.name;
  const owned = pitcher.team_name;
  const isFa = pitcher.is_free_agent !== false && !owned;
  const statLine = buildProbableStatLine(pitcher);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{
          fontSize: '11px',
          fontWeight: '600',
          color: isTbd
            ? dynastyTheme.tokens.colors.neutral[500]
            : dynastyTheme.tokens.colors.neutral[100],
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{name}</span>
        {owned ? (
          <span style={{
            fontSize: '7px', fontWeight: 700, padding: '1px 3px', borderRadius: 2,
            color: C.white, backgroundColor: dynastyTheme.tokens.colors.info, flexShrink: 0,
          }}>{owned.substring(0, 10)}</span>
        ) : isFa && !isTbd ? (
          <span style={{
            fontSize: '7px', fontWeight: 700, padding: '1px 3px', borderRadius: 2,
            color: C.white, backgroundColor: dynastyTheme.tokens.colors.success, flexShrink: 0,
          }}>FA</span>
        ) : null}
      </div>
      {statLine && (
        <span style={{
          fontSize: '9px',
          color: dynastyTheme.tokens.colors.neutral[400],
          marginTop: '1px',
        }}>{statLine}</span>
      )}
    </div>
  );
};

const GamesTabContent = ({ leagues, defaultLeagueId, onNavigate = null }) => {
  const [games, setGames] = useState([]);
  const [probables, setProbables] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [probLoading, setProbLoading] = useState(true);

  const leagueId = defaultLeagueId || (leagues?.length > 0 ? leagues[0].league_id : null);
  const teamId = useMemo(() => {
    if (!leagues || !leagueId) return null;
    const league = leagues.find(l => l.league_id === leagueId);
    return league?.team_id || null;
  }, [leagues, leagueId]);

  useEffect(() => {
    loadGames();
    loadProbables();
  }, [leagueId, teamId]);

  const loadGames = async () => {
    setGamesLoading(true);
    try {
      const res = await mlbAPI.getTodaysGames();
      setGames(res?.games || []);
    } catch { setGames([]); }
    finally { setGamesLoading(false); }
  };

  const loadProbables = async () => {
    if (!leagueId || !teamId) { setProbLoading(false); return; }
    setProbLoading(true);
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const start = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
      const res = await fetchProbablePitchers(leagueId, teamId, start, 4);
      setProbables(res?.games || []);
    } catch { setProbables([]); }
    finally { setProbLoading(false); }
  };

  // Group probables by date
  const groupedProbables = useMemo(() => {
    const groups = {};
    probables.forEach(g => {
      const d = g.game_date || 'Unknown';
      if (!groups[d]) groups[d] = [];
      groups[d].push(g);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [probables]);

  return (
    <>
      {/* Today's Games — with ownership badges */}
      <Section title="Today's Games" maxHeight="280px">
        {gamesLoading ? <Spinner text="Loading games..." /> :
          games.length === 0 ? <EmptyState text="No games today" /> :
          games.map((g, i) => (
            <div key={g.game_id || i} style={{
              padding: '6px 12px',
              borderBottom: i < games.length - 1 ? `0.5px solid ${dynastyTheme.tokens.colors.neutral[700]}` : 'none',
            }}>
              {/* Teams + score + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: dynastyTheme.tokens.colors.neutral[50], width: '32px' }}>{g.away_team}</span>
                {g.away_score != null && <span style={{ fontSize: '12px', fontWeight: '600', color: dynastyTheme.tokens.colors.primary, width: '16px', textAlign: 'center' }}>{g.away_score}</span>}
                <span style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[500], flex: 1, textAlign: 'center' }}>
                  {g.abstract_state === 'Live' ? `${g.inning_state === 'top' ? 'Top' : 'Bot'} ${g.inning}` : g.abstract_state === 'Final' ? 'Final' : g.game_time || 'TBD'}
                </span>
                {g.home_score != null && <span style={{ fontSize: '12px', fontWeight: '600', color: dynastyTheme.tokens.colors.primary, width: '16px', textAlign: 'center' }}>{g.home_score}</span>}
                <span style={{ fontSize: '13px', fontWeight: '700', color: dynastyTheme.tokens.colors.neutral[50], width: '32px', textAlign: 'right' }}>{g.home_team}</span>
              </div>
              {/* Pitchers with ownership */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <PitcherBadge pitcher={g.away_pitcher} />
                <span style={{ fontSize: '9px', color: dynastyTheme.tokens.colors.neutral[600] }}>vs</span>
                <PitcherBadge pitcher={g.home_pitcher} />
              </div>
            </div>
          ))}
      </Section>

      {/* Upcoming Probables — grouped by date, fixed-height scroll */}
      <div style={{ padding: '0 8px', marginBottom: '14px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: dynastyTheme.tokens.colors.neutral[50], marginBottom: '8px' }}>Upcoming Probables</h3>
        <div
          className="home-scroll"
          style={{
            ...mobileTileStyle,
            maxHeight: '400px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            position: 'relative',
          }}
        >
          {probLoading ? <Spinner text="Loading probables..." /> :
            groupedProbables.length === 0 ? <EmptyState text={!leagueId ? 'Set a default league' : 'No upcoming probables'} /> :
            groupedProbables.map(([dateKey, dateGames]) => (
              <div key={dateKey}>
                {/* Date header */}
                <div style={{
                  padding: '5px 12px',
                  backgroundColor: 'transparent',
                  borderBottom: `0.5px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                  position: 'sticky', top: 0, zIndex: 1,
                }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: dynastyTheme.tokens.colors.primary }}>{formatGameDate(dateKey)}</span>
                </div>
                {/* Games for this date */}
                {dateGames.map((g, i) => (
                  <div key={g.game_id || i} style={{
                    padding: '6px 12px',
                    borderBottom: `0.5px solid ${dynastyTheme.tokens.colors.neutral[700]}30`,
                  }}>
                    {/* Row 1: teams + time */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: dynastyTheme.tokens.colors.neutral[50] }}>
                        {g.away_team} @ {g.home_team}
                      </span>
                      <span style={{ fontSize: '9px', color: dynastyTheme.tokens.colors.neutral[500] }}>
                        {g.game_time || 'TBD'}
                      </span>
                    </div>
                    {/* Row 2: pitchers stacked (name + pill + stat line each) */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <ProbablePitcherDisplay pitcher={g.away_pitcher} />
                      <span style={{ fontSize: '9px', color: dynastyTheme.tokens.colors.neutral[600], paddingTop: '2px' }}>vs</span>
                      <ProbablePitcherDisplay pitcher={g.home_pitcher} />
                    </div>
                  </div>
                ))}
              </div>
            ))
          }
        </div>
        {/* Scroll indicator */}
        {groupedProbables.length > 0 && probables.length > 6 && (
          <div style={{ textAlign: 'center', padding: '4px 0', fontSize: '9px', color: dynastyTheme.tokens.colors.neutral[600] }}>
            Scroll for more
          </div>
        )}
        {/* 9n: link to dedicated MobileProbables page. Only shown when onNavigate
            is wired (i.e., when News is rendered inside MobileApp). */}
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('probables')}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '8px',
              padding: '10px',
              background: 'transparent',
              border: `1px solid ${dynastyTheme.tokens.colors.primary}40`,
              borderRadius: '6px',
              color: dynastyTheme.tokens.colors.primary,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            View All Probables →
          </button>
        )}
      </div>
    </>
  );
};

// ============================================================
// TRENDING TAB CONTENT
// ============================================================
const TrendingTabContent = () => {
  const [hotHitters, setHotHitters] = useState([]);
  const [hotPitchers, setHotPitchers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTrending(); }, []);

  const loadTrending = async () => {
    setLoading(true);
    try {
      const [hitRes, pitchRes] = await Promise.all([
        mlbAPI.getTrendingPlayers(),
        mlbAPI.getTrendingPlayers('?player_type=pitching'),
      ]);
      setHotHitters(hitRes?.hot_players || []);
      setHotPitchers(pitchRes?.hot_players || []);
    } catch {
      setHotHitters([]); setHotPitchers([]);
    } finally { setLoading(false); }
  };

  if (loading) return <Spinner text="Loading trending..." />;

  // Bible Rule 1: backend returns canonical-nested player objects per
  // services/mlb_data_service.py:_build_canonical. Read p.info.* for player
  // metadata, not flat top-level fields. The badge shows the sequential rank
  // (1..N) from the .map() index since the backend doesn't return a numeric
  // hotness_score — hot/cold is derived from top/bottom slice of OPS/ERA.
  const PlayerRow = ({ p, i, total }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
      borderBottom: i < total - 1 ? `1px solid ${dynastyTheme.tokens.colors.neutral[700]}30` : 'none',
    }}>
      <div style={{
        width: '24px', height: '24px', borderRadius: '50%',
        backgroundColor: dynastyTheme.tokens.colors.error + '20',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '10px', fontWeight: '700', color: dynastyTheme.tokens.colors.error, flexShrink: 0,
      }}>
        {i + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: '12px', fontWeight: '600', color: dynastyTheme.tokens.colors.neutral[50],
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {p.info?.full_name || 'Unknown'}
        </p>
        <p style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[500] }}>
          {p.info?.position} · {p.info?.mlb_team}
        </p>
      </div>
      <span style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[400], flexShrink: 0 }}>
        {p.classification}
      </span>
    </div>
  );

  return (
    <>
      <Section title="Hot Hitters" maxHeight="200px">
        {hotHitters.length === 0 ? <EmptyState text="No trending hitters" /> :
          hotHitters.map((p, i) => <PlayerRow key={p.ids?.mlb || i} p={p} i={i} total={hotHitters.length} />)}
      </Section>
      <Section title="Hot Pitchers" maxHeight="200px">
        {hotPitchers.length === 0 ? <EmptyState text="No trending pitchers" /> :
          hotPitchers.map((p, i) => <PlayerRow key={p.ids?.mlb || i} p={p} i={i} total={hotPitchers.length} />)}
      </Section>
    </>
  );
};

// ============================================================
// INJURIES TAB CONTENT
// ============================================================
const InjuriesTabContent = () => {
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadInjuries(); }, []);

  const loadInjuries = async () => {
    setLoading(true);
    try {
      const res = await mlbAPI.getInjuryReport();
      setInjuries(res?.injuries || []);
    } catch { setInjuries([]); }
    finally { setLoading(false); }
  };

  if (loading) return <Spinner text="Loading injuries..." />;

  return (
    <Section>
      {injuries.length === 0 ? <EmptyState text="No active injuries" /> :
        injuries.map((inj, i) => (
          <div key={inj.player_id || i} style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
            borderBottom: i < injuries.length - 1 ? `1px solid ${dynastyTheme.tokens.colors.neutral[700]}30` : 'none',
          }}>
            {/* Status dot */}
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: (inj.status || '').toLowerCase().includes('day-to-day')
                ? dynastyTheme.tokens.colors.warning
                : dynastyTheme.tokens.colors.error,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontSize: '12px', fontWeight: '600', color: dynastyTheme.tokens.colors.neutral[50],
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {inj.name || inj.full_name}
                <span style={{ fontWeight: '400', color: dynastyTheme.tokens.colors.neutral[500] }}>
                  {' '}{inj.position} · {inj.team}
                </span>
              </p>
              <p style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[400] }}>
                {inj.status || inj.injury_status} — {inj.injury || inj.injury_description || 'Undisclosed'}
              </p>
            </div>
            {(inj.return_date || inj.expected_return) && (
              <span style={{
                fontSize: '9px', color: dynastyTheme.tokens.colors.neutral[500], flexShrink: 0,
              }}>
                {formatDate(inj.return_date || inj.expected_return)}
              </span>
            )}
          </div>
        ))}
    </Section>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
function MobileNews({ onNavigate = null }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('news');
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoaded, setLeaguesLoaded] = useState(false);
  const [league, setLeague] = useState(null);

  const { defaultLeagueId } = useDefaultLeague(leagues, true);

  useEffect(() => {
    const loadLeagues = async () => {
      try {
        const res = await leaguesAPI.getMyLeagues();
        if (res?.success && res.leagues) setLeagues(res.leagues);
      } catch (err) {
        console.error('Error loading leagues:', err);
      } finally {
        setLeaguesLoaded(true);
      }
    };
    loadLeagues();
  }, []);

  // Load league details for nav bar context
  useEffect(() => {
    if (defaultLeagueId) {
      leaguesAPI.getLeagueDetails(defaultLeagueId)
        .then(res => { if (res?.success && res.league) setLeague(res.league); })
        .catch(() => {});
    }
  }, [defaultLeagueId]);

  // League nav bar handler — navigate to league page on that section
  const handleLeagueNavigate = useCallback((pageId) => {
    if (defaultLeagueId) {
      navigate(`/leagues/${defaultLeagueId}`, { state: { initialPage: pageId } });
    }
  }, [defaultLeagueId, navigate]);

  return (
    <div style={{
      backgroundColor: 'transparent',
    }}>
      <style>{`
        .home-scroll::-webkit-scrollbar { display: none; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      {/* League Navigation Bar */}
      {defaultLeagueId && (
        <MobileLeagueNavigation currentPage="news" onNavigate={handleLeagueNavigate} />
      )}

      {/* Shared header tile */}
      <MobilePageHeader
        title={league?.league_name || 'News'}
        pageBadge="News"
        bannerUrl={league?.league_settings?.league_banner_url}
        showLogo={false}
      />

      {/* Top tabs */}
      <TabBar
        tabs={[
          { id: 'news', label: 'News' },
          { id: 'games', label: 'Games' },
          { id: 'trending', label: 'Trending' },
          { id: 'injuries', label: 'Injuries' },
        ]}
        active={activeTab}
        onChange={setActiveTab}
      />

      {/* Tab content */}
      {activeTab === 'news' && leaguesLoaded && (
        <NewsTabContent leagues={leagues} defaultLeagueId={defaultLeagueId} />
      )}
      {activeTab === 'games' && leaguesLoaded && (
        <GamesTabContent leagues={leagues} defaultLeagueId={defaultLeagueId} onNavigate={onNavigate} />
      )}
      {activeTab === 'trending' && <TrendingTabContent />}
      {activeTab === 'injuries' && <InjuriesTabContent />}
    </div>
  );
}

export default MobileNews;
